import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import {
	DraftCarton,
	PickOrderLine,
	PickedQuantities,
	ShortfallDisposition,
	ShortfallDispositions,
	dispositionFor,
	hasReportableOutcome,
	nonEmptyCartons,
	pickedFor,
	shortPickedLines,
	shortfallFor,
	totalUnpacked,
} from '../draft'
import { cartonBreakdown, packItems, remainingBreakdown } from '../packing'

export type CompleteStepProps = {
	lines: PickOrderLine[],
	picked: PickedQuantities,
	cartons: DraftCarton[],
	/** What to do with each short line's missing units. Absent means leave it open. */
	dispositions: ShortfallDispositions,
	onDispositionChange: ( lineId: string, disposition: ShortfallDisposition ) => void,
}

/**
 * The last look before the order closes: what is going out, in how many
 * cartons, with which tracking numbers (if any), and what came up short.
 *
 * Each carton names the orders it fulfils, because that is the fact a packer
 * cannot recover once the box is taped — and the one the app decided for them.
 *
 * Everything here is a fact about this run, with one exception: what happens to
 * the units that were not found is a decision, and it is taken here. Shorting a
 * line closes its fulfillment order without those units, which is not something
 * to infer from a count, so each short line is put to the picker with both
 * outcomes named.
 *
 * What completing does is said by the button and the footer beside it, and a
 * tracking number announces itself as optional on the field the packer types it
 * into, so neither is repeated as a standing note.
 */
export default function CompleteStep( {
	lines,
	picked,
	cartons,
	dispositions,
	onDispositionChange,
}: CompleteStepProps ) {
	const shipping = nonEmptyCartons( cartons )
	const shortLines = shortPickedLines( lines, picked )
	const unpacked = totalUnpacked( lines, picked, cartons )
	const leftOnBench = packItems( lines, picked, cartons, null ).filter( ( item ) => item.remaining > 0 )
	const unitsShipping = shipping.reduce(
		( sum, carton ) => sum + Object.values( carton.contents ).reduce( ( count, quantity ) => count + quantity, 0 ),
		0,
	)
	const reportable = hasReportableOutcome( lines, picked, cartons, dispositions )

	return (
		<Stack spacing={2}>
			{!reportable && (
				<Alert severity="warning">
					Nothing to ship or short. Pack a carton, short a line, or cancel the pick order.
				</Alert>
			)}

			<Paper variant="outlined" sx={{ p: 2.5, borderColor: 'pickFlow.cardBorder', bgcolor: 'pickFlow.surface' }}>
				{shipping.length === 0 ? (
					<Typography variant="body2" color="text.secondary">
						Nothing is being shipped.
					</Typography>
				) : (
					// A total, not a tally of the list below it: "1 carton" sitting on
					// top of "Carton 1" was the same words twice and read as a label
					// for the first box rather than a summary of them all.
					<Typography variant="h6" sx={{ mb: 2 }}>
						{unitsShipping} {unitsShipping === 1 ? 'unit' : 'units'} in{' '}
						{shipping.length} {shipping.length === 1 ? 'carton' : 'cartons'}
					</Typography>
				)}
				<Stack spacing={2} divider={<Divider />}>
					{shipping.map( ( carton, index ) => {
						const contents = packItems( lines, picked, cartons, carton.key )
							.filter( ( item ) => item.inThisCarton > 0 )
						return (
							<Box key={carton.key}>
								<Typography variant="body1" fontWeight={700}>Carton {index + 1}</Typography>
								<Typography variant="body2" color="text.secondary">
									{carton.trackingNumber.trim() || 'No tracking number'}
								</Typography>
								<Stack spacing={0.5} sx={{ pl: 2, pt: 0.5 }}>
									{contents.map( ( item ) => (
										<Stack
											key={item.productId}
											direction="row"
											spacing={1.5}
											alignItems="center"
											justifyContent="space-between"
										>
											<Box sx={{ minWidth: 0 }}>
												<Typography variant="body2">
													{item.identity.sku ?? 'No part number'} · {item.identity.description}
												</Typography>
												<Typography variant="body2" color="text.secondary">
													{cartonBreakdown( item )}
												</Typography>
											</Box>
											<Typography variant="body1" fontWeight={700} sx={{ flexShrink: 0 }}>
												{item.inThisCarton}
											</Typography>
										</Stack>
									) )}
								</Stack>
							</Box>
						)
					} )}
				</Stack>
			</Paper>

			{unpacked > 0 && (
				<Alert severity="warning">
					<Typography variant="body2" sx={{ mb: leftOnBench.length > 0 ? 1 : 0 }}>
						{unpacked} picked {unpacked === 1 ? 'unit isn’t' : 'units aren’t'} in a carton and won’t ship.
					</Typography>
					<Stack spacing={0.5}>
						{leftOnBench.map( ( item ) => (
							<Typography key={item.productId} variant="body2">
								{item.identity.sku ?? 'No part number'} · {item.identity.description} — {remainingBreakdown( item )}
							</Typography>
						) )}
					</Stack>
				</Alert>
			)}

			{shortLines.length > 0 && (
				<Paper
					variant="outlined"
					sx={{ p: 2.5, borderColor: 'pickFlow.shortPickBorder', bgcolor: 'pickFlow.shortPickBackground' }}
				>
					<Typography variant="h6" gutterBottom>Short picks</Typography>
					<Typography variant="body2" sx={{ mb: 2 }}>
						Choose what happens to the missing units.
					</Typography>
					<Stack spacing={2}>
						{shortLines.map( ( line ) => {
							const disposition = dispositionFor( dispositions, line.id )
							const missing = shortfallFor( line, picked )
							const orderName = `FO-${line.fulfillmentOrder.orderNumber}`
							return (
								<Box key={line.id}>
									<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
										<Chip size="small" label={orderName} />
										<Typography variant="body2">
											{line.product.name} — found {pickedFor( picked, line.id )} of {line.quantityToPick}
										</Typography>
									</Stack>
									<ToggleButtonGroup
										exclusive
										size="small"
										value={disposition}
										// A group with nothing selected returns null; there is no
										// third outcome, so an attempt to deselect is ignored.
										onChange={( _event, next ) => next && onDispositionChange( line.id, next )}
										aria-label={`What to do with the ${missing} not found for ${line.product.name} on ${orderName}`}
									>
										<ToggleButton value="leaveOpen">Leave open</ToggleButton>
										<ToggleButton value="short">Short the order</ToggleButton>
									</ToggleButtonGroup>
									<Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
										{disposition === 'short'
											? `${orderName} closes without the missing ${missing}.`
											: `${orderName} stays open for the missing ${missing}.`}
									</Typography>
								</Box>
							)
						} )}
					</Stack>
				</Paper>
			)}
		</Stack>
	)
}
